/**
 * expr 的**解释器**。
 *
 * ## 为什么不用 `new Function`
 *
 * 这一版只做 expr（受限表达式）。它在两件事上比 `new Function` 好，
 * 而且都不是「更安全一点点」那种好：
 *
 *   ① **CSP 友好** —— 源码不经过 JS 引擎，任何禁 `unsafe-eval` 的宿主都能跑；
 *   ② **是真沙箱** —— 能碰到什么完全由白名单决定（`whitelist.ts` + `helpers.ts`），
 *      摸不到 `globalThis` / 原型链。
 *      而 `new Function` 即使「显式注入作用域」也只是卫生，不是安全。
 *
 * 代价是表达力：没有语句、没有自定义函数、没有循环。
 * 但**图里已经有循环节点和组了** —— 复用的方式是把它们连起来，
 * 而不是在表达式里写 `for`。这个代价比看起来小。
 *
 * ## 三层防线
 *
 *   ① 编译期：未知标识符、非法调用（`parse.ts` 的 `validate`）
 *   ② 求值期：危险键（下面 `blockedKey`）—— 挡 `x.__proto__` 这类
 *   ③ 兜底：`evaluateGraph` 已经按节点 try/catch，抛出去只会变成
 *      那一个节点上的诊断，不会带崩整张图
 *
 * ## 比较语义（和 JS 有意不同）
 *
 * `<` `<=` `>` `>=` 用**数值优先**：两边都能当数字就按数字比，
 * 否则按字符串。原因是 JS 的默认行为会踩这个坑 ——
 * 数据源给 `9`（number），控件里手输 `"10"`（string），
 * 直接比字符串会判 `"9" > "10"` 为真。这种错**不报错**，只是结果不对。
 *
 * `===` / `!==` 是严格相等，不做转换（要转换就显式 `num(a) === num(b)`）。
 * `==` / `!=` 在编译期就被拒了。
 */
import type { ExprNode } from './ast'
import { HELPERS } from './helpers'
import type { NodeEvalApi } from '../evaluate'

/** 一旦读到这些键就直接拒绝 —— 原型链逃逸的入口 */
const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype'])

export class ExprRuntimeError extends Error {}

function blockedKey(key: string): boolean {
  return BLOCKED_KEYS.has(key)
}

/** 数值优先比较：两边都能当数字就按数字，否则按字符串 */
function compare(op: '<' | '<=' | '>' | '>=', left: unknown, right: unknown): boolean {
  const a = toNumberLike(left)
  const b = toNumberLike(right)
  if (a !== null && b !== null) {
    switch (op) {
      case '<':
        return a < b
      case '<=':
        return a <= b
      case '>':
        return a > b
      case '>=':
        return a >= b
    }
  }
  const x = String(left)
  const y = String(right)
  switch (op) {
    case '<':
      return x < y
    case '<=':
      return x <= y
    case '>':
      return x > y
    case '>=':
      return x >= y
  }
}

/**
 * 「能不能当数字看」。
 *
 * ⚠️ 和 `listOps.toNumber` **不同**：那个把 `true` 当 1、空串当 null。
 * 这里只认数字本身和「看起来是数字的字符串」——
 * 比较 `true > 0` 这种事在模板里没有意义，不该悄悄成立。
 */
function toNumberLike(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const text = value.trim()
    if (text === '') return null
    const n = Number(text)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * 解释执行。
 *
 * `api` 由宿主（`evaluateGraph`）给，`helpers` 用的是白名单那一份 ——
 * 两者都是**显式**递进去的，不存在「从外层作用域摸一个变量」的可能。
 */
export function evaluateExpr(node: ExprNode, api: NodeEvalApi): unknown {
  switch (node.kind) {
    case 'lit':
      return node.value

    case 'ref':
      if (node.name === 'api') return api
      if (node.name === 'helpers') return HELPERS
      /* 编译期已经挡过了；留一条是为了「编译产物被手改」这种情况 */
      throw new ExprRuntimeError(`未知标识符「${node.name}」`)

    case 'member': {
      const object = evaluateExpr(node.object, api)
      if (blockedKey(node.property)) {
        throw new ExprRuntimeError(`不许访问 \`${node.property}\``)
      }
      if (object === null || object === undefined) return undefined
      return (object as Record<string, unknown>)[node.property]
    }

    case 'index': {
      const object = evaluateExpr(node.object, api)
      const key = evaluateExpr(node.index, api)
      const text = String(key)
      if (blockedKey(text)) throw new ExprRuntimeError(`不许访问 \`${text}\``)
      if (object === null || object === undefined) return undefined
      return (object as Record<string, unknown>)[text]
    }

    case 'call': {
      /*
       * ⚠️ 被调者只可能是 `api.x` / `helpers.x`（编译期保证）。
       * 这里**再算一次**是因为编译产物理论上可以被绕过 ——
       * 多一层判断比信任上游便宜。
       */
      if (node.callee.kind !== 'member' || node.callee.object.kind !== 'ref') {
        throw new ExprRuntimeError('只能调用 api / helpers 上的方法')
      }
      const rootName = node.callee.object.name
      const fnName = node.callee.property
      const root = rootName === 'api' ? api : rootName === 'helpers' ? HELPERS : null
      if (!root) throw new ExprRuntimeError(`不能调用「${rootName}」上的东西`)
      if (blockedKey(fnName)) throw new ExprRuntimeError(`不许调用 \`${fnName}\``)
      const fn = (root as Record<string, unknown>)[fnName]
      if (typeof fn !== 'function') {
        throw new ExprRuntimeError(`\`${rootName}.${fnName}\` 不是方法`)
      }
      const args = node.args.map((arg) => evaluateExpr(arg, api))
      return (fn as (...a: unknown[]) => unknown).apply(root, args)
    }

    case 'unary': {
      const value = evaluateExpr(node.operand, api)
      if (node.op === '!') return !value
      const n = toNumberLike(value) ?? 0
      return node.op === '-' ? -n : n
    }

    case 'binary': {
      /* `&&` / `||` 短路 —— 和 JS 一致，右边可能根本不求值 */
      if (node.op === '&&') {
        const left = evaluateExpr(node.left, api)
        return left ? evaluateExpr(node.right, api) : left
      }
      if (node.op === '||') {
        const left = evaluateExpr(node.left, api)
        return left ? left : evaluateExpr(node.right, api)
      }

      const left = evaluateExpr(node.left, api)
      const right = evaluateExpr(node.right, api)
      switch (node.op) {
        case '===':
          return left === right
        case '!==':
          return left !== right
        case '<':
        case '<=':
        case '>':
        case '>=':
          return compare(node.op, left, right)
        case '+':
          /*
           * `+` 三种情况分开处理，**不做** JS 那种「一边是字符串就都转字符串」：
           *   · 两边都是数字（或能当数字）→ 加法
           *   · 否则 → 拼接
           * ⚠️ 这样 `"1" + 2` 是 `"12"`（拼接）还是 `3`（加法）？
           * 取**拼接**：字面量字符串出现时人的意图几乎总是拼。
           * 要加法就写 `num("1") + 2`。
           */
          if (typeof left === 'string' || typeof right === 'string') {
            return stringify(left) + stringify(right)
          }
          if (Array.isArray(left) && Array.isArray(right)) return [...left, ...right]
          return (toNumberLike(left) ?? 0) + (toNumberLike(right) ?? 0)
        case '-':
          return (toNumberLike(left) ?? 0) - (toNumberLike(right) ?? 0)
        case '*':
          return (toNumberLike(left) ?? 0) * (toNumberLike(right) ?? 0)
        case '/': {
          const divisor = toNumberLike(right) ?? 0
          /* 除零给 null 而不是 Infinity：Infinity 会一路传到输出里 */
          if (divisor === 0) return null
          return (toNumberLike(left) ?? 0) / divisor
        }
        case '%': {
          const divisor = toNumberLike(right) ?? 0
          if (divisor === 0) return null
          return (toNumberLike(left) ?? 0) % divisor
        }
      }
      return undefined
    }

    case 'cond':
      return evaluateExpr(node.test, api) ? evaluateExpr(node.yes, api) : evaluateExpr(node.no, api)

    case 'array':
      return node.items.map((item) => evaluateExpr(item, api))

    case 'object': {
      const out: Record<string, unknown> = {}
      for (const entry of node.entries) {
        if (blockedKey(entry.key)) throw new ExprRuntimeError(`不许用键 \`${entry.key}\``)
        out[entry.key] = evaluateExpr(entry.value, api)
      }
      return out
    }

    case 'template':
      return node.parts
        .map((part) => (typeof part === 'string' ? part : stringify(evaluateExpr(part, api))))
        .join('')
  }
}

/** 模板拼接时的转换：`null` / `undefined` 给空串（不是 "null"） */
function stringify(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map((item) => stringify(item)).join(', ')
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}
